/**
 * Iter105: AI-Vorschläge / Smart Actions für die Command Palette.
 *
 * Erkennt natürlich-sprachliche Queries und schlägt Aktionen mit Direktnavigation vor.
 * Verwendet Pattern-Matching auf Schlüsselwörter — keine LLM-Calls nötig (sofort + offline).
 *
 * Beispiele:
 *   "offene Rechnungen"        → /invoices?status=open
 *   "überfällige Rechnungen"   → /invoices?status=overdue
 *   "rechnungen >30 tage"      → /invoices?status=overdue&min_days=30
 *   "belege diesen monat"      → /receipts?period=current_month
 *   "unbuchte belege"          → /receipts?status=unreviewed
 *   "lohnabrechnung märz"      → /payroll?month=03
 *   "mwst q1"                  → /reports/vat?quarter=1
 *   "bilanz 2025"              → /reports?type=balance_sheet&year=2025
 *   "neue rechnung"            → /invoices/new (Action)
 *   "neuer beleg"              → /receipts (mit Upload-Dialog)
 */

const MONTH_NAMES = {
  januar: 1, jan: 1,
  februar: 2, feb: 2,
  märz: 3, maerz: 3, mär: 3, mar: 3,
  april: 4, apr: 4,
  mai: 5,
  juni: 6, jun: 6,
  juli: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  oktober: 10, okt: 10, oct: 10,
  november: 11, nov: 11,
  dezember: 12, dez: 12, dec: 12,
};

const CURRENT_YEAR = new Date().getFullYear();

function extractDays(q) {
  // Erkennt "30 Tage", ">30 tage", "> 30 t", etc.
  const m = q.match(/>\s*(\d+)\s*(?:tage?|t\b)/i) || q.match(/(\d+)\s*tage?/i);
  return m ? parseInt(m[1], 10) : null;
}

function extractYear(q) {
  const m = q.match(/\b(20\d{2})\b/);
  return m ? parseInt(m[1], 10) : null;
}

function extractQuarter(q) {
  const m = q.match(/q\s*([1-4])\b/i) || q.match(/quartal\s*([1-4])/i);
  return m ? parseInt(m[1], 10) : null;
}

function extractMonth(q) {
  const lower = q.toLowerCase();
  for (const [name, num] of Object.entries(MONTH_NAMES)) {
    if (lower.includes(name)) return num;
  }
  return null;
}

/** Returns Array of suggestions: { id, label, hint, url, score } */
export function buildAiSuggestions(rawQuery) {
  if (!rawQuery || rawQuery.length < 3) return [];
  const q = rawQuery.toLowerCase().trim();
  const suggestions = [];

  // === Invoices ===
  if (q.includes("rechnung") && !q.includes("eingangsr")) {
    if (q.includes("überfällig") || q.includes("uberfallig") || q.includes("overdue")) {
      const days = extractDays(q);
      suggestions.push({
        id: "ai-invoices-overdue",
        label: days ? `Überfällige Rechnungen (>${days} Tage)` : "Überfällige Rechnungen",
        hint: "Status=overdue im Filter",
        url: days ? `/invoices?status=overdue&min_days=${days}` : "/invoices?status=overdue",
        score: 100,
      });
    } else if (q.includes("offen") || q.includes("open")) {
      suggestions.push({
        id: "ai-invoices-open",
        label: "Offene Rechnungen",
        hint: "Status=sent, noch nicht bezahlt",
        url: "/invoices?status=open",
        score: 100,
      });
    } else if (q.includes("bezahlt") || q.includes("paid")) {
      suggestions.push({
        id: "ai-invoices-paid",
        label: "Bezahlte Rechnungen",
        hint: "Status=paid",
        url: "/invoices?status=paid",
        score: 100,
      });
    } else if (q.includes("entwurf") || q.includes("draft")) {
      suggestions.push({
        id: "ai-invoices-draft",
        label: "Rechnungs-Entwürfe",
        hint: "Status=draft (noch nicht versendet)",
        url: "/invoices?status=draft",
        score: 100,
      });
    }
    if (q.startsWith("neu") || q.startsWith("erstell") || q.includes("anlegen")) {
      suggestions.push({
        id: "ai-invoices-new",
        label: "Neue Rechnung erstellen",
        hint: "Öffnet Rechnungs-Dialog",
        url: "/invoices?new=1",
        score: 95,
      });
    }
  }

  // === Bills (Eingangsrechnungen) ===
  if (q.includes("eingangsr") || q.includes("lieferantenr") || q.includes("kreditor")) {
    suggestions.push({
      id: "ai-bills-list",
      label: "Eingangsrechnungen",
      hint: "Alle Bills",
      url: "/bills",
      score: 90,
    });
    if (q.includes("offen") || q.includes("zu zahlen")) {
      suggestions.push({
        id: "ai-bills-open",
        label: "Offene Eingangsrechnungen",
        hint: "Noch nicht bezahlt",
        url: "/bills?status=open",
        score: 100,
      });
    }
  }

  // === Belege ===
  if (q.includes("beleg") || q.includes("quittung") || q.includes("receipt")) {
    if (q.includes("unbuch") || q.includes("ungeprüf") || q.includes("offen")) {
      suggestions.push({
        id: "ai-receipts-unreviewed",
        label: "Ungeprüfte Belege",
        hint: "Status=unreviewed",
        url: "/receipts?status=unreviewed",
        score: 100,
      });
    }
    if (q.startsWith("neu") || q.includes("hochlad") || q.includes("scan")) {
      suggestions.push({
        id: "ai-receipts-new",
        label: "Beleg hochladen / scannen",
        hint: "OCR + KI-Auto-Buchung",
        url: "/receipts?upload=1",
        score: 95,
      });
    }
  }

  // === Mahnwesen ===
  if (q.includes("mahnung") || q.includes("dunning") || q.includes("erinnerung")) {
    suggestions.push({
      id: "ai-dunning",
      label: "Mahnwesen öffnen",
      hint: "Überfällige + Mahnstufen",
      url: "/dunning",
      score: 100,
    });
  }

  // === MWST ===
  if (q.includes("mwst") || q.includes("mehrwertsteuer") || q.includes("vat")) {
    const q4 = extractQuarter(q);
    if (q4) {
      suggestions.push({
        id: `ai-vat-q${q4}`,
        label: `MWST-Abrechnung Q${q4}`,
        hint: `Quartal ${q4} ${CURRENT_YEAR}`,
        url: `/reports/vat?quarter=${q4}&year=${CURRENT_YEAR}`,
        score: 100,
      });
    } else {
      suggestions.push({
        id: "ai-vat",
        label: "MWST-Auswertung",
        hint: "Aktuelles Quartal",
        url: "/reports/vat",
        score: 90,
      });
    }
  }

  // === Bilanz / Erfolgsrechnung ===
  if (q.includes("bilanz") || q.includes("balance")) {
    const yr = extractYear(q);
    suggestions.push({
      id: "ai-balance-sheet",
      label: yr ? `Bilanz ${yr}` : "Bilanz aktuelles Jahr",
      hint: "Aktiven/Passiven nach KMU-Schema",
      url: yr ? `/reports?type=balance_sheet&year=${yr}` : `/reports?type=balance_sheet`,
      score: 100,
    });
  }
  if (q.includes("erfolg") || q.includes("gewinn") || q.includes("verlust") || q.includes("er ")) {
    const yr = extractYear(q);
    suggestions.push({
      id: "ai-income-statement",
      label: yr ? `Erfolgsrechnung ${yr}` : "Erfolgsrechnung aktuelles Jahr",
      hint: "Ertrag/Aufwand",
      url: yr ? `/reports?type=income_statement&year=${yr}` : `/reports?type=income_statement`,
      score: 100,
    });
  }

  // === Lohn ===
  if (q.includes("lohn") || q.includes("gehalt") || q.includes("payroll")) {
    const m = extractMonth(q);
    if (m) {
      suggestions.push({
        id: `ai-payroll-month-${m}`,
        label: `Lohnabrechnung ${Object.keys(MONTH_NAMES).find((k) => MONTH_NAMES[k] === m) || ""}`,
        hint: `Monat ${String(m).padStart(2, "0")}`,
        url: `/payroll?month=${String(m).padStart(2, "0")}&year=${CURRENT_YEAR}`,
        score: 100,
      });
    } else {
      suggestions.push({
        id: "ai-payroll",
        label: "Lohnbuchhaltung öffnen",
        hint: "Lohnläufe + Mitarbeiter",
        url: "/payroll",
        score: 90,
      });
    }
  }

  // === Cashflow / Liquidität ===
  if (q.includes("cashflow") || q.includes("liquid") || q.includes("prognose") || q.includes("forecast")) {
    const days = extractDays(q) || 90;
    suggestions.push({
      id: "ai-cashflow",
      label: `Liquiditätsprognose (${days} Tage)`,
      hint: "Cashflow-Forecast auf Dashboard",
      url: `/?cashflow=${days}`,
      score: 100,
    });
  }

  // === Bank ===
  if (q.includes("bank") || q.includes("abstimm")) {
    suggestions.push({
      id: "ai-bank",
      label: "Bank-Abstimmung öffnen",
      hint: "camt.053 Abgleich",
      url: "/bank-reconciliation",
      score: 90,
    });
  }

  // === Kasse ===
  if (q.includes("kass") || q.includes("bar")) {
    suggestions.push({
      id: "ai-cashbook",
      label: "Kassenbuch",
      hint: "Bareinnahmen/-ausgaben",
      url: "/cashbook",
      score: 90,
    });
  }

  // Dedupe nach id, sortiere absteigend nach score
  const seen = new Set();
  return suggestions
    .filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}
